import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { userReportsAPI } from '../services/api';
import './UserReportModal.css';

interface UserReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportedUserId: number;
  reportedUserName: string;
  reportedUserPhoto?: string | null;
  reportedUserPhone?: string | null;
}

const getPhotoUrl = (photoPath?: string | null) => {
  if (!photoPath) return null;
  if (photoPath.startsWith('http')) return photoPath;
  return `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000'}${photoPath}`;
};

const UserReportModal: React.FC<UserReportModalProps> = ({
  isOpen,
  onClose,
  reportedUserId,
  reportedUserName,
  reportedUserPhoto,
  reportedUserPhone,
}) => {
  const { t } = useLanguage();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reasons = [
    { value: 'harassment', label: t('userReport.reasons.harassment') },
    { value: 'inappropriate_content', label: t('userReport.reasons.inappropriateContent') },
    { value: 'fake_profile', label: t('userReport.reasons.fakeProfile') },
    { value: 'child_safety', label: t('userReport.reasons.childSafety') },
    { value: 'spam', label: t('userReport.reasons.spam') },
    { value: 'other', label: t('userReport.reasons.other') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason) {
      setError(t('userReport.errors.reasonRequired'));
      return;
    }
    if (!description.trim()) {
      setError(t('userReport.errors.descriptionRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await userReportsAPI.create(reportedUserId, reason, description.trim());
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset state after close
        setReason('');
        setDescription('');
        setSuccess(false);
        setError('');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('userReport.errors.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setReason('');
    setDescription('');
    setSuccess(false);
    setError('');
  };

  return (
    <div className="report-modal-overlay" onClick={handleClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <h2>{t('userReport.title')}</h2>
          <button className="report-modal-close" onClick={handleClose}>
            &times;
          </button>
        </div>

        {success ? (
          <div className="report-success">
            <div className="report-success-icon">&#10003;</div>
            <p>{t('userReport.successMessage')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="report-form">
            <div className="report-user-info">
              {reportedUserPhoto ? (
                <img
                  src={getPhotoUrl(reportedUserPhoto) || undefined}
                  alt={reportedUserName}
                  className="report-user-photo"
                />
              ) : (
                <div className="report-user-photo-placeholder">
                  {reportedUserName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="report-user-details">
                <p className="report-user-name">{reportedUserName}</p>
                {reportedUserPhone && (
                  <p className="report-user-phone">{reportedUserPhone}</p>
                )}
              </div>
            </div>

            <div className="report-field">
              <label>{t('userReport.reasonLabel')}</label>
              <div className="report-reasons">
                {reasons.map((r) => (
                  <label
                    key={r.value}
                    className={`report-reason-option ${reason === r.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="report-field">
              <label>{t('userReport.descriptionLabel')}</label>
              <textarea
                className="report-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('userReport.descriptionPlaceholder')}
                rows={4}
                maxLength={1000}
              />
              <span className="report-char-count">{description.length}/1000</span>
            </div>

            {error && <p className="report-error">{error}</p>}

            <div className="report-actions">
              <button
                type="button"
                className="report-btn-cancel"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="report-btn-submit"
                disabled={isSubmitting || !reason || !description.trim()}
              >
                {isSubmitting ? t('userReport.submitting') : t('userReport.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserReportModal;
